/**
 * +glide: the hero's glider layer (pure, no three.js, no allocation per frame).
 *
 * While the glider is open the hero hangs under it: both arms raised overhead holding the lines
 * (elbows soft, hands in), the torso tipped a little back, the head looking down the glide, legs
 * dangling and swaying (alternating, slower when slow), and the whole body banking into turns. The
 * deploy pops in (weight eases up fast, a small overshoot); folding or touching down eases out. A
 * landing from a real fall plays a short settle (knees give, the torso dips, the arms come down).
 *
 * Offsets are in the clip channel space (anim/clips.ts) and are added on top of the clip + idle sum
 * by HeroView, so ability clips (Dodge) and the idle breathing still read through.
 */
import { CH_HOP, NCH, ch } from './clips';

const clamp = (x: number, a: number, b: number): number => (x < a ? a : x > b ? b : x);
const approach = (cur: number, target: number, rate: number, dt: number): number => cur + (target - cur) * (1 - Math.exp(-rate * dt));

/** The hanging pose at full weight (radians; see the channel table in clips.ts). */
export const GLIDE_POSE: Readonly<Record<string, readonly [number, number, number]>> = {
  spine: [-0.12, 0, 0],
  chest: [-0.06, 0, 0],
  head: [0.22, 0, 0],
  leftUpperArm: [2.35, -0.25, 0.55],
  rightUpperArm: [2.35, -0.25, 0.55],
  leftLowerArm: [0.55, 0, 0],
  rightLowerArm: [0.55, 0, 0],
  leftHand: [0, 0, -0.35],
  rightHand: [0, 0, -0.35],
  leftUpperLeg: [0.32, 0, 0.06],
  rightUpperLeg: [0.18, 0, 0.06],
  leftLowerLeg: [0.45, 0, 0],
  rightLowerLeg: [0.3, 0, 0],
  leftFoot: [0.35, 0, 0],
  rightFoot: [0.3, 0, 0],
};
const POSE_IDX: readonly [number, number][] = Object.entries(GLIDE_POSE).flatMap(([b, v]) => v.map((x, a): [number, number] => [ch(b as never, a as 0 | 1 | 2), x]));

/** Landing settle: duration (s) and depth. */
export const SETTLE_S = 0.42;

export interface GlideInput {
  dt: number;
  /** The glider is open now. */
  gliding: boolean;
  /** Airborne (any free flight or a hop): the settle plays when this ends after a real fall. */
  airborne: boolean;
  /** Height over the ground (yd). */
  height: number;
  /** Ground speed (yd/s). */
  speed: number;
  /** Turn rate of the facing (rad/s, + = turning left) and sideways drift (yd/s, + = right). */
  turnRate: number;
  lateral: number;
}

export class GlideLayer {
  /** 0..1: how open the glider is (the pose weight; the canopy scales with `canopy`). */
  w = 0;
  /** The canopy's scale (pops past 1 on deploy). */
  canopy = 0;
  private canopyV = 0;
  /** Bank (radians, + = rolling right), eased. */
  bank = 0;
  /** Landing settle 0..1 (1 at the touch-down). */
  settle = 0;
  private t = 0;
  private fell = 0;
  private wasAir = false;
  readonly out = new Float32Array(NCH);

  update(i: GlideInput): void {
    const dt = clamp(i.dt, 0, 0.1);
    this.t += dt;
    // Deploy fast, fold a touch slower (the canopy collapses after the arms drop).
    this.w = approach(this.w, i.gliding ? 1 : 0, i.gliding ? 14 : 9, dt);
    // The canopy: a damped spring toward open / folded (overshoots a little: the "pop").
    const target = i.gliding ? 1 : 0;
    this.canopyV += ((target - this.canopy) * 260 - this.canopyV * 17) * dt;
    this.canopy = clamp(this.canopy + this.canopyV * dt, 0, 1.25);
    if (!i.gliding && this.canopy < 0.02) this.canopy = this.canopyV = 0;
    this.bank = approach(this.bank, clamp(-i.turnRate * 0.28 + i.lateral * 0.07, -0.55, 0.55), 5, dt);
    // A settle when a real fall ends (a glide, or a drop of a couple of yards).
    if (i.airborne) this.fell = Math.max(this.fell, i.height);
    if (this.wasAir && !i.airborne && this.fell > 1.6) this.settle = 1;
    if (!i.airborne && !this.wasAir) this.fell = 0;
    this.wasAir = i.airborne;
    if (!i.airborne) this.fell = 0;
    this.settle = Math.max(0, this.settle - dt / SETTLE_S);

    const o = this.out;
    o.fill(0);
    const w = this.w;
    if (w > 1e-3) {
      for (const [c, v] of POSE_IDX) o[c] = v * w;
      // Dangling legs: an alternating sway, livelier at speed; a slow pendulum of the whole body.
      const sp = clamp(i.speed / 9, 0, 1.3);
      const swing = Math.sin(this.t * (2.1 + sp)) * (0.12 + 0.1 * sp);
      o[ch('leftUpperLeg', 0)]! += swing * w;
      o[ch('rightUpperLeg', 0)]! -= swing * w;
      o[ch('leftLowerLeg', 0)]! += Math.max(0, swing) * 0.6 * w;
      o[ch('rightLowerLeg', 0)]! += Math.max(0, -swing) * 0.6 * w;
      // Banking: the body leans into the turn, the inside arm pulls its line down.
      const b = this.bank * w;
      o[ch('spine', 2)]! += b * 0.6;
      o[ch('hips', 2)]! += b * 0.3;
      o[ch('leftUpperArm', 0)]! -= Math.max(0, -b) * 0.5;
      o[ch('rightUpperArm', 0)]! -= Math.max(0, b) * 0.5;
      o[ch('head', 1)]! += -b * 0.4;
      // A gentle bob under the canopy.
      o[CH_HOP]! += Math.sin(this.t * 1.3) * 0.05 * w;
    }
    const s = this.settle;
    if (s > 0) {
      // Knees give and come back (a soft landing), the torso dips, the arms swing down from overhead.
      const k = Math.sin((1 - s) * Math.PI) * (0.4 + 0.6 * s);
      o[ch('leftUpperLeg', 0)]! += 0.55 * k;
      o[ch('rightUpperLeg', 0)]! += 0.45 * k;
      o[ch('leftLowerLeg', 0)]! += 0.95 * k;
      o[ch('rightLowerLeg', 0)]! += 0.85 * k;
      o[ch('spine', 0)]! += 0.25 * k;
      o[ch('leftUpperArm', 2)]! += 0.4 * k;
      o[ch('rightUpperArm', 2)]! += 0.4 * k;
      o[CH_HOP]! -= 0.16 * k;
    }
  }
}
